import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LockKey } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      toast.success(data.message);
      navigate("/auth");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-16" data-testid="reset-password-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="glass-panel p-10 w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <LockKey size={26} weight="duotone" className="text-gold" />
          <h1 className="font-heading text-3xl font-semibold tracking-tighter">Forge a new key</h1>
        </div>
        {!token ? (
          <p className="text-sm text-muted-foreground" data-testid="reset-missing-token">
            This reset link is invalid. <Link to="/auth" className="text-gold hover:underline">Request a new one</Link>.
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">New password</label>
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" data-testid="reset-password-input"
                className="w-full bg-black/40 border border-white/15 px-4 py-3 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-gold/60 transition-colors duration-300"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Confirm password</label>
              <input
                type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••" data-testid="reset-confirm-input"
                className="w-full bg-black/40 border border-white/15 px-4 py-3 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-gold/60 transition-colors duration-300"
              />
            </div>
            {error && (
              <p className="text-xs text-red-400 border border-red-500/30 bg-red-500/10 px-3 py-2" data-testid="reset-error-message">{error}</p>
            )}
            <button
              type="submit" disabled={loading} data-testid="reset-submit-button"
              className="w-full py-4 rounded-full bg-gold text-black text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#e8c95a] disabled:opacity-50 transition-colors duration-300"
            >
              {loading ? "Sealing..." : "Set new password"}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
